import {StringHelper} from "@bryntum/schedulerpro";
import {isObjDeepEqual} from "../../utils/utils.jsx";
import {getInlineStyleInObj} from "../../utils/domUtils.jsx";
import diffJson from "diff-json";
import { PLUGINS_KEYS } from "../../constants/PluginKeys.jsx";

// not using ObjectHelper.clone as it attempts copies functions or other complex type of data as well other then simple type of data
const cloneStates = (state) => JSON.parse(JSON.stringify(state)); // ObjectHelper.clone(newState.store)

class ViewSharedUtil {
  static localStateKeyPrefix = "swivu:views";

  static isStoreStateChanged(oldState, newState) {
    if (!oldState || !newState) return true;

    // Note: We assign a default value of {} to `store` because in Bryntum version 6.1.9,
    // the `view_state` property loses its nested objects.
    // If we set the `view_state` property's type to "object", a replay effect occurs.
    const { columns: oldStateColumns, store: oldStateStore = {} } = oldState;
    const { columns: newStateColumns, store: newStateStore } = newState;
    const oldStateTimeAxisConfig = { tickSize: oldState.tickSize, zoomLevel: oldState.zoomLevel };
    const newStateTimeAxisConfig = { tickSize: newState.tickSize, zoomLevel: newState.zoomLevel };

    const oldStateFeaturesConfig = oldState.features;
    const newStateFeaturesConfig = newState.features;
    // zoomLevelOptions {centerDate, endDate, startDate, width (same as tickSize)} - not checking for viewstate in these as tickSize and zoomLevel
    // are enough, but then if user changes shared view zoomLevel then that will be persisted in viewstate as well

    if (!isObjDeepEqual(oldStateTimeAxisConfig, newStateTimeAxisConfig)) {
      console.log(diffJson.diff(oldStateTimeAxisConfig, newStateTimeAxisConfig), "-diff-timeaxis-");
      return true;
    }

    if (oldStateColumns && newStateColumns && !isObjDeepEqual(oldStateColumns, newStateColumns)) {
      console.log(diffJson.diff(oldStateColumns, newStateColumns), "-diff-columns-");
      return true;
    }

    if (oldStateStore && newStateStore) {
      const oldStateClone = this.cleanViewState({ store: oldStateStore });
      const newStateClone = this.cleanViewState({ store: newStateStore });

      // we don't need to apply sortFilters on group records as there can be only one group value in the groupers array
      console.log(diffJson.diff(oldStateClone, newStateClone), "-diff-store-");
      if (!isObjDeepEqual(oldStateClone, newStateClone)) {
        return true;
      }
    }

    if (oldStateFeaturesConfig && newStateFeaturesConfig) {
      const oldStateClone = cloneStates(this.cleanTimelineConfig(oldStateFeaturesConfig));
      const newStateClone = cloneStates(this.cleanTimelineConfig(newStateFeaturesConfig));

      // we don't need to apply sortFilters on group records as there can be only one group value in the groupers array
      console.log(diffJson.diff(oldStateClone, newStateClone), "-diff-features-");
      return !isObjDeepEqual(oldStateClone, newStateClone);
    }
  }

  static cleanTimelineConfig(timeAxisConfigState) {
    // Todo Note: this function or following lines of code can be removed entirely, as previously following configs were saved in view state
    // but it then changed to be saved in user preference
    if (timeAxisConfigState && timeAxisConfigState[PLUGINS_KEYS.swivuTimelineConfigurator]) {
      delete timeAxisConfigState[PLUGINS_KEYS.swivuTimelineConfigurator].scaleDragCopiedTaskUsingMajorTicks;
      delete timeAxisConfigState[PLUGINS_KEYS.swivuTimelineConfigurator].scaleNewTaskEventsUsingMajorTickline;
    }
    return timeAxisConfigState;
  }

  static cleanViewState(allState, storeModelClass) {
    const finalState = cloneStates(allState);

    // assigning sourceField to each of the store records
    if (finalState.store && storeModelClass) {
      finalState.store = ViewSharedUtil.assignSourceFieldToStoreItems(finalState.store, storeModelClass);
    }

    const sanitize = (store, sanitizeKey) => {
      if (store[sanitizeKey] && Array.isArray(store[sanitizeKey])) {
        // we don't want to persist hidden filters and also want to sort filters otherwise improper indexes trigger view state change modal
        const updatedState = store[sanitizeKey].filter((filter) => !filter.hidden);
        if (!updatedState.length) return delete store[sanitizeKey];
        else store[sanitizeKey] = updatedState;
      }
    }

    // with our new column multi filter,
    // the order of filters gets changed whenever we try to open the filter popup from column which cause isDeeplyEqual to notify a change
    sanitize(finalState.store, "filters");
    // more specifically, hidden sorters are applied from timeaxis view of scheduler
    sanitize(finalState.store, "sorters");
    sanitize(finalState.store, "groupers");

    if (finalState.store?.filters) {
      finalState.store.filters = finalState.store.filters.map((filter, idx) => {
        // Had to add an `id` manually as add filters from Grid -> Column -> Filter Menu doesn't assign id to filter
        // Copied the id assignment statement from Filter -> onStoreBeforeFilter function
        if (!filter.id) filter.id = `${filter.property}-${idx}-${Date.now()}`;
        return filter;
      }).sort((a, b) => a.id.localeCompare(b.id));
    }

    return finalState;
  }

  static saveToLocalStorageForKey(key, state) {
    const localState = StringHelper.safeJsonParse(localStorage.getItem(this.localStateKeyPrefix)) || {};
    localState[key] = state;
    localStorage.setItem(this.localStateKeyPrefix, StringHelper.safeJsonStringify(localState));
  }

  static getFromLocalStorageForKey(key, defaultValue) {
    const localState = StringHelper.safeJsonParse(localStorage.getItem(this.localStateKeyPrefix));
    if (localState) {
      return localState[key] || defaultValue;
    }
    return defaultValue;
  }

  static reorderColumnPositions(fromOrder, toOrder) {
    return toOrder.reduce((finalCols, toColOrder) => {
      const col = fromOrder.find((fromColOrder) => fromColOrder.id === toColOrder.id);
      if (col) { // can be null in case when we may have a column not exist in our grid but exist in saved state
        finalCols.push(col);
      }
      return finalCols;
    }, []);
  }

  static getNestedArrayTypeStateProcess(stateToUpdate, stateToInclude, initialDefaultState) {
    const initialDefColsState = initialDefaultState[stateToInclude.name] || [];
    const toUpdateColsState = stateToUpdate[stateToInclude.name];

    return initialDefColsState.map((initialDefState) => {
      const toUpdateColState = toUpdateColsState.find((colState) => colState.id === initialDefState.id);
      const finalMergedState = { ...initialDefState, ...toUpdateColState };
      return this.#pickNestTypeStateProperties(finalMergedState, stateToInclude.statesToInclude);
    });
  }

  static mergeNestedArrayTypeStateProcess(currentProcessedState, stateToInclude, initialDefaultState) {
    const initialDefColsState = initialDefaultState[stateToInclude.name] || [];
    const toUpdateColsState = currentProcessedState[stateToInclude.name];

    return initialDefColsState.map((initialDefState) => {
      const toUpdateColState = toUpdateColsState.find((colState) => colState.id === initialDefState.id);
      const finalMergedState = { ...initialDefState, ...toUpdateColState };
      // there is an issue with having both flex and width in column, we need to discard flex if column has a width otherwise switching between views cause columns with to break
      if (finalMergedState.width && finalMergedState.flex) delete finalMergedState.flex;
      return finalMergedState;
    });
  }

  static getInlineStyleValueForLocalState(stateToUpdate, stateToInclude) {
    const styleInObj = getInlineStyleInObj(stateToUpdate.style);

    return styleInObj.length ? stateToInclude.reduce((accum, stateToInclude) => {
      const propertyIdx = styleInObj.findIndex((i) => i.includes(stateToInclude));
      if (propertyIdx !== -1) accum.push(styleInObj[propertyIdx])
      return accum;
    }, []): [];
  }

  static mergeInlineStyleValueForLocalState(localState, stateToInclude, initialDefaultState) {
    let defaultStyles = getInlineStyleInObj(initialDefaultState.style);
    const localStateStyles = localState.style || [];

    if (localStateStyles.length) {
      stateToInclude.forEach((stateToInclude) => {
        defaultStyles = defaultStyles.filter((i) => !i.includes(stateToInclude));
      });
    }

    return [...defaultStyles, ...localStateStyles].join(" ");
  }

  static #pickNestTypeStateProperties(obj, props) {
    return props.reduce((result, prop) => {
      if (prop in obj) {
        result[prop] = obj[prop];
      }
      return result;
    }, {});
  }

  static getSourceField(storeModelClass, fieldName) {
    if (fieldName) {
      return storeModelClass.swivuGetFieldOriginalSourceName?.(fieldName) || storeModelClass.getFieldDefinition(fieldName)?.dataSource || fieldName;
    }
    return fieldName;
  }

  static assignSourceFieldToStoreItems(storeState, storeModelClass) {
    const newStoreState = { ...storeState };

    if (storeState.filters) {
      newStoreState.filters = storeState.filters.map((filterItem) => ({ ...filterItem, sourceField: ViewSharedUtil.getSourceField(storeModelClass, filterItem.property) }));
    }
    if (storeState.sorters) {
      newStoreState.sorters = storeState.sorters.map((sorterItem) => ({ ...sorterItem, sourceField: ViewSharedUtil.getSourceField(storeModelClass, sorterItem.field) }));
    }
    if (storeState.groupers) {
      newStoreState.groupers = storeState.groupers.map((grouperItem) => ({ ...grouperItem, sourceField: ViewSharedUtil.getSourceField(storeModelClass, grouperItem.field) }));
    }

    return newStoreState;
  }

  static getNestedObjectTypeState(currentState, stateToInclude) {
    const prevObjState = currentState[stateToInclude.name];
    const toUpdateObjState = {};
    stateToInclude.statesToInclude.forEach((stateToIncludeItem) => {
      if (typeof stateToIncludeItem === "object") {
        if (prevObjState[stateToIncludeItem.name]?.length) {
          toUpdateObjState[stateToIncludeItem.name] = prevObjState[stateToIncludeItem.name].map((stateItem) => {
            const state = {};
            stateToIncludeItem.statesToInclude.forEach((itemToInclude) => {
              state[itemToInclude] = stateItem[itemToInclude]
            })
            return state;
          })
        }
      } else {
        toUpdateObjState[stateToIncludeItem] = prevObjState[stateToIncludeItem] || []
      }
    })
    return toUpdateObjState;
  }
}

export default ViewSharedUtil;
